import ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import { Chart, PieController, ArcElement, Tooltip, Legend, Title } from "chart.js"
import { SurveyItemRaw } from "./reports-page-types"
import { normalizeData } from "./visitor-profile-helper"

Chart.register(PieController, ArcElement, Tooltip, Legend, Title)

const HEADER_FILL: ExcelJS.FillPattern = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFF5E1E" },
}

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: "FFFFFFFF" },
  name: "Arial",
  size: 11,
}

const TITLE_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  name: "Arial",
  size: 13,
  color: { argb: "FFFF5E1E" },
}

const CELL_FONT: Partial<ExcelJS.Font> = {
  name: "Arial",
  size: 11,
}

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
}

const CENTER_ALIGN: Partial<ExcelJS.Alignment> = {
  horizontal: "center",
  vertical: "middle",
  wrapText: true,
}

const PIE_COLORS = [
  "#4472C4",
  "#ED7D31",
  "#A5A5A5",
  "#FFC000",
  "#5B9BD5",
  "#70AD47",
  "#264478",
  "#9B59B6",
]

function renderPieChartToBase64(title: string, labels: string[], values: number[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas")
    canvas.width = 700
    canvas.height = 500

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      reject(new Error("Could not get canvas 2d context"))
      return
    }

    ctx.fillStyle = "#FFFFFF"
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const chart = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: PIE_COLORS.slice(0, labels.length),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        plugins: {
          title: {
            display: true,
            text: title,
            font: { size: 18, weight: "bold" },
            color: "#FF5E1E",
          },
          legend: {
            position: "right",
            labels: { font: { size: 13 } },
          },
        },
      },
    })

    requestAnimationFrame(() => {
      const base64 = canvas.toDataURL("image/png")
      chart.destroy()
      const raw = base64.replace(/^data:image\/png;base64,/, "")
      resolve(raw)
    })
  })
}

export async function generateProfileSurveyXlsx(rawData: SurveyItemRaw[], fileName: string): Promise<void> {
  const data = normalizeData(rawData)
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet("Dados")
  let currentRow = 1

  for (const item of data) {
    const answerKeys = Object.keys(item.answers)
    const totalCols = answerKeys.length + 1

    ws.mergeCells(currentRow, 1, currentRow, totalCols)
    const titleCell = ws.getCell(currentRow, 1)
    titleCell.value = item.question
    titleCell.font = TITLE_FONT
    currentRow++

    const headers = ["Total", ...answerKeys]
    headers.forEach((header, idx) => {
      const cell = ws.getCell(currentRow, idx + 1)
      cell.value = header
      cell.font = HEADER_FONT
      cell.fill = HEADER_FILL
      cell.alignment = CENTER_ALIGN
      cell.border = THIN_BORDER
    })
    currentRow++

    const totalCell = ws.getCell(currentRow, 1)
    totalCell.value = item.total
    totalCell.font = CELL_FONT
    totalCell.alignment = CENTER_ALIGN
    totalCell.border = THIN_BORDER

    answerKeys.forEach((key, idx) => {
      const cell = ws.getCell(currentRow, idx + 2)
      cell.value = item.answers[key]
      cell.font = CELL_FONT
      cell.alignment = CENTER_ALIGN
      cell.border = THIN_BORDER
    })

    currentRow += 2
  }

  ws.getColumn(1).width = 14
  for (let col = 2; col <= 20; col++) {
    ws.getColumn(col).width = 22
  }

  for (const item of data) {
    const filtered = Object.entries(item.answers).filter(([, v]) => v > 0)
    if (filtered.length === 0) continue

    const labels = filtered.map(([k]) => k)
    const values = filtered.map(([, v]) => v)
    const sheetName = item.question
      .replace("Pergunta sobre ", "")
      .slice(0, 31)
    const cs = wb.addWorksheet(sheetName)

    const headerA = cs.getCell(1, 1)
    headerA.value = "Resposta"
    headerA.font = { ...CELL_FONT, bold: true }

    const headerB = cs.getCell(1, 2)
    headerB.value = "Contagem"
    headerB.font = { ...CELL_FONT, bold: true }

    filtered.forEach(([label, val], idx) => {
      cs.getCell(idx + 2, 1).value = label
      cs.getCell(idx + 2, 1).font = CELL_FONT
      cs.getCell(idx + 2, 2).value = val
      cs.getCell(idx + 2, 2).font = CELL_FONT
    })

    cs.getColumn(1).width = 35
    cs.getColumn(2).width = 12

    const base64Png = await renderPieChartToBase64(
      item.question,
      labels,
      values
    )

    const imageId = wb.addImage({
      base64: base64Png,
      extension: "png",
    })

    cs.addImage(imageId, {
      tl: { col: 3, row: 0 },
      ext: { width: 700, height: 500 },
    })
  }

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  saveAs(blob, `${fileName}.xlsx`)
}
